import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Add, Delete, Edit, Storefront, Star, StarBorder } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getAuthHeaders, makeApiCall } from '../utils/apiConfig';
import { ConfirmDialog, EmptyState, FormDialog, SectionCard } from './ui';
import { LocationDiscoveryList } from './LocationDiscoveryList';
import { fetchStoreTeamMembers, StoreTeamMember } from '../services/storeTeamApi';
import { platformLabel } from '../utils/storePlatform';

export interface StoreLocation {
  id: string;
  name: string;
  code: string | null;
  address: { street: string; city: string; state: string; zip: string; country: string };
  phone: string | null;
  email: string | null;
  isPrimary: boolean;
  isActive: boolean;
  integrationIds: string[];
}

interface IntegrationSummary {
  id: string;
  platform?: string;
  storeName?: string;
  name?: string;
  shopDomain?: string;
  credentials?: { shopDomain?: string };
}

const emptyForm = {
  name: '',
  code: '',
  street: '',
  city: '',
  state: '',
  zip: '',
  country: '',
  phone: '',
  email: '',
  isActive: true,
  integrationIds: [] as string[],
};

const readError = async (response: Response, fallback: string): Promise<string> => {
  const body = await response.json().catch(() => ({}));
  return body?.error?.message || body?.message || fallback;
};

const integrationLabel = (i: IntegrationSummary): string =>
  `${i.platform ? platformLabel(i.platform) : 'Integration'}${
    i.storeName || i.name || i.shopDomain || i.credentials?.shopDomain ? ` · ${i.storeName || i.name || i.shopDomain || i.credentials?.shopDomain}` : ''
  }`;

export const STORE_LOCATIONS_QUERY_KEY = ['store-locations'] as const;

/**
 * Jordan's locations: the places the store sells from. The primary one is
 * where pricing and rebates apply when nothing names a location; each
 * location can say which connected integrations serve it.
 */
export const StoreLocationsCard: React.FC = () => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<StoreLocation | 'new' | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [removing, setRemoving] = useState<StoreLocation | null>(null);
  const [error, setError] = useState<string | null>(null);

  const locations = useQuery({
    queryKey: STORE_LOCATIONS_QUERY_KEY,
    queryFn: async () => {
      const response = await makeApiCall('/store/locations', { headers: await getAuthHeaders() });
      if (!response.ok) throw new Error(await readError(response, 'Failed to load locations'));
      return ((await response.json()).data || []) as StoreLocation[];
    },
  });

  const integrations = useQuery({
    queryKey: ['integrations'],
    queryFn: async () => {
      const response = await makeApiCall('/integrations', { headers: await getAuthHeaders() });
      if (!response.ok) return [] as IntegrationSummary[];
      return ((await response.json()).data || []) as IntegrationSummary[];
    },
  });
  const integrationById = useMemo(() => new Map((integrations.data || []).map((i) => [i.id, i])), [integrations.data]);

  // Who works where. A member with no locations works at every one of them,
  // so they are listed under each location.
  const members = useQuery<StoreTeamMember[]>({
    queryKey: ['organization-members'],
    queryFn: fetchStoreTeamMembers,
    // The team list is admin-only; without it the card simply shows no names
    retry: false,
  });
  const membersByLocation = useMemo(() => {
    const map = new Map<string, string[]>();
    const everyLocation = (locations.data || []).map((l) => l.id);
    for (const member of members.data || []) {
      const name = member.displayName || member.email || 'Unknown member';
      const assigned = member.locationIds && member.locationIds.length > 0 ? member.locationIds : everyLocation;
      for (const locationId of assigned) {
        map.set(locationId, [...(map.get(locationId) || []), name]);
      }
    }
    return map;
  }, [members.data, locations.data]);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: STORE_LOCATIONS_QUERY_KEY });

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        name: form.name,
        code: form.code || null,
        address: { street: form.street, city: form.city, state: form.state, zip: form.zip, country: form.country },
        phone: form.phone || null,
        email: form.email || null,
        isActive: form.isActive,
        integrationIds: form.integrationIds,
      };
      const isNew = editing === 'new';
      const response = await makeApiCall(isNew ? '/store/locations' : `/store/locations/${(editing as StoreLocation).id}`, {
        method: isNew ? 'POST' : 'PUT',
        headers: await getAuthHeaders(),
        body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error(await readError(response, 'Save failed'));
      return response.json();
    },
    onSuccess: () => { setEditing(null); setError(null); invalidate(); },
    onError: (e: any) => setError(e?.message || 'Save failed'),
  });

  const makePrimary = useMutation({
    mutationFn: async (id: string) => {
      const response = await makeApiCall(`/store/locations/${id}`, { method: 'PUT', headers: await getAuthHeaders(), body: JSON.stringify({ isPrimary: true }) });
      if (!response.ok) throw new Error(await readError(response, 'Could not set primary'));
      return response.json();
    },
    onSuccess: () => { setError(null); invalidate(); },
    onError: (e: any) => setError(e?.message || 'Could not set primary'),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const response = await makeApiCall(`/store/locations/${id}`, { method: 'DELETE', headers: await getAuthHeaders() });
      if (!response.ok) throw new Error(await readError(response, 'Remove failed'));
      return response.json();
    },
    onSuccess: () => { setRemoving(null); setError(null); invalidate(); },
    onError: (e: any) => { setRemoving(null); setError(e?.message || 'Remove failed'); },
  });

  const openNew = () => { setForm(emptyForm); setEditing('new'); };
  const openEdit = (l: StoreLocation) => {
    setForm({
      name: l.name,
      code: l.code || '',
      street: l.address.street,
      city: l.address.city,
      state: l.address.state,
      zip: l.address.zip,
      country: l.address.country,
      phone: l.phone || '',
      email: l.email || '',
      isActive: l.isActive,
      integrationIds: l.integrationIds,
    });
    setEditing(l);
  };

  const field = (key: keyof typeof emptyForm, label: string, extra: Record<string, unknown> = {}) => (
    <TextField
      size="small"
      label={label}
      value={form[key] as string}
      onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
      fullWidth
      {...extra}
    />
  );

  return (
    <SectionCard
      title="Locations"
      subtitle="Where you sell from. Pricing and rebates can be set for one location; the primary one applies when nothing names a location."
      leading={<Storefront color="primary" />}
      actions={<Button size="small" variant="outlined" startIcon={<Add />} onClick={openNew} sx={{ whiteSpace: 'nowrap' }}>Add location</Button>}
    >
      {error && <Typography variant="body2" color="error" sx={{ mb: 1.5 }}>{error}</Typography>}
      <LocationDiscoveryList integrations={integrations.data || []} onAccepted={invalidate} />
      {locations.isLoading ? null : !locations.data || locations.data.length === 0 ? (
        <EmptyState title="No locations yet" description="Add the stores and warehouses you sell from." size="compact" />
      ) : (
        <Stack spacing={1.5}>
          {locations.data.map((l) => (
            <Box key={l.id} sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="body1" fontWeight={600}>{l.name}</Typography>
                  {l.code && <Chip size="small" variant="outlined" label={l.code} />}
                  {l.isPrimary && <Chip size="small" color="primary" label="Primary" icon={<Star />} />}
                  {!l.isActive && <Chip size="small" variant="outlined" label="Inactive" />}
                </Stack>
                <Typography variant="body2" color="text.secondary">
                  {[l.address.street, [l.address.city, l.address.state].filter(Boolean).join(', '), l.address.zip].filter(Boolean).join(' · ') || 'No address'}
                </Typography>
                {(membersByLocation.get(l.id) || []).length > 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Assigned: {(membersByLocation.get(l.id) || []).join(', ')}
                  </Typography>
                )}
                {l.integrationIds.length > 0 && (
                  <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }} flexWrap="wrap">
                    {l.integrationIds.map((id) => (
                      <Chip key={id} size="small" variant="outlined" label={integrationById.get(id) ? integrationLabel(integrationById.get(id)!) : id} />
                    ))}
                  </Stack>
                )}
              </Box>
              <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                {!l.isPrimary && (
                  <Tooltip title="Make primary">
                    <IconButton size="small" aria-label={`Make ${l.name} primary`} onClick={() => makePrimary.mutate(l.id)}><StarBorder fontSize="small" /></IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Edit">
                  <IconButton size="small" aria-label={`Edit ${l.name}`} onClick={() => openEdit(l)}><Edit fontSize="small" /></IconButton>
                </Tooltip>
                {!l.isPrimary && (
                  <Tooltip title="Remove">
                    <IconButton size="small" aria-label={`Remove ${l.name}`} onClick={() => setRemoving(l)}><Delete fontSize="small" /></IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Box>
          ))}
        </Stack>
      )}

      <FormDialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'Add location' : 'Edit location'}
        onSubmit={() => save.mutate()}
        submitLabel={save.isPending ? 'Saving…' : 'Save'}
        submitDisabled={!form.name.trim() || save.isPending}
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {field('name', 'Name', { required: true, autoFocus: true })}
            {field('code', 'Code (optional)', { sx: { maxWidth: { sm: 180 } } })}
          </Stack>
          {field('street', 'Street')}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {field('city', 'City')}
            {field('state', 'State')}
            {field('zip', 'ZIP')}
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {field('country', 'Country')}
            {field('phone', 'Phone')}
          </Stack>
          {field('email', 'Email', { type: 'email' })}
          <FormControl size="small" fullWidth>
            <InputLabel id="location-integrations-label">Integrations serving this location</InputLabel>
            <Select
              labelId="location-integrations-label"
              label="Integrations serving this location"
              multiple
              value={form.integrationIds}
              onChange={(e) => setForm((f) => ({ ...f, integrationIds: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value }))}
              renderValue={(ids) => (ids as string[]).map((id) => (integrationById.get(id) ? integrationLabel(integrationById.get(id)!) : id)).join(', ')}
            >
              {(integrations.data || []).map((i) => (
                <MenuItem key={i.id} value={i.id}>{integrationLabel(i)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {editing !== 'new' && editing && !editing.isPrimary && (
            <FormControlLabel control={<Switch checked={form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} />} label="Active" />
          )}
        </Stack>
      </FormDialog>

      <ConfirmDialog
        open={!!removing}
        title="Remove location"
        message={<>Remove <strong>{removing?.name}</strong>? Pricing set for this location alone will no longer apply.</>}
        confirmLabel="Remove"
        confirmColor="error"
        loading={remove.isPending}
        onConfirm={() => removing && remove.mutate(removing.id)}
        onClose={() => setRemoving(null)}
      />
    </SectionCard>
  );
};

export default StoreLocationsCard;
